// apps/web/src/features/clients/components/ClientRestoreImpactView.tsx
//
// What a restore would actually do, named record by record (AC-11). Totals
// alone ("3 records will change") force the operator to diff two screens in
// their head, which is exactly the mistake this preview exists to prevent.
//
// INVARIANT: this view renders `clientRestoreImpact` verbatim. It never claims
// bank name/BIC/account or set history come back — the snapshot does not record
// them — and a legal entity the snapshot needs but that no longer exists is
// shown as the server's REFUSAL, not as something the restore will re-create.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '../../../ui/index.js';
import {
  isEmptyRestoreImpact,
  type ClientRestoreImpact,
  type ClientRestoreRootChange,
  type ClientRestoreRow,
} from '../clientVersionImpact.js';

// `as const` keeps the literal key types, which is what `t()` needs to resolve
// a lookup-table key instead of an opaque `string`.
const ROOT_LABEL = {
  name: 'clients:restore.rootName',
  comment: 'clients:restore.rootComment',
  archive: 'clients:restore.rootArchive',
  unarchive: 'clients:restore.rootUnarchive',
} as const satisfies Record<ClientRestoreRootChange, string>;

const GROUP_TITLE_STYLE = {
  margin: 0,
  fontSize: 'var(--fz-11)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  color: 'var(--fg-3)',
};

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div data-testid="client-restore-group">
      <p style={GROUP_TITLE_STYLE}>{title}</p>
      <ul style={{ margin: 0, paddingInlineStart: 'var(--s-6)', fontSize: 'var(--fz-13)' }}>
        {children}
      </ul>
    </div>
  );
}

export function ClientRestoreImpactView({ impact }: { impact: ClientRestoreImpact }) {
  const { t } = useTranslation(['clients']);

  /**
   * One affected record. Two contacts may share a name, so the row is keyed by
   * kind + id and carries the phone/INN that tells them apart; a rename is
   * shown as "current → recorded", never as the future name alone.
   */
  const rows = (list: readonly ClientRestoreRow[]) =>
    list.map((row) => (
      <li key={`${row.kind}:${row.id}`} data-testid="client-restore-row">
        {t(row.kind === 'contact' ? 'clients:restore.contactLabel' : 'clients:restore.legalLabel', {
          name: row.name,
        })}
        {row.detail !== undefined && <span style={{ color: 'var(--fg-3)' }}> · {row.detail}</span>}
        {row.recordedName !== undefined && (
          <span> {t('clients:restore.renamedTo', { name: row.recordedName })}</span>
        )}
      </li>
    ));

  return (
    <div
      data-testid="client-restore-impact"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)', minWidth: 0 }}
    >
      {impact.blocking.length > 0 && (
        <div data-testid="client-restore-blocking">
          <Banner tone="warn">
            {t('clients:restore.missingLegal', {
              names: impact.blocking.map((row) => row.name).join(', '),
            })}
          </Banner>
        </div>
      )}

      {isEmptyRestoreImpact(impact) ? (
        <Banner tone="info">{t('clients:restore.noChanges')}</Banner>
      ) : (
        <Banner tone="info">
          {/* Each effect is counted as itself: a record that no longer exists
              is RE-CREATED, which is not the same as one coming back from the
              archive, and rolling them together would misstate the write. */}
          {t('clients:restore.summary', {
            changed: impact.changed.length,
            archived: impact.archived.length,
            restored: impact.restored.length,
            recreated: impact.recreated.length,
          })}
        </Banner>
      )}

      {impact.rootChanges.length > 0 && (
        <Group title={t('clients:restore.rootTitle')}>
          {impact.rootChanges.map((change) => (
            <li key={change} data-testid="client-restore-row">
              {t(ROOT_LABEL[change])}
            </li>
          ))}
        </Group>
      )}
      {impact.changed.length > 0 && (
        <Group title={t('clients:restore.changedTitle')}>{rows(impact.changed)}</Group>
      )}
      {impact.archived.length > 0 && (
        <Group title={t('clients:restore.archivedTitle')}>{rows(impact.archived)}</Group>
      )}
      {impact.restored.length > 0 && (
        <Group title={t('clients:restore.restoredTitle')}>{rows(impact.restored)}</Group>
      )}
      {impact.recreated.length > 0 && (
        <Group title={t('clients:restore.recreatedTitle')}>{rows(impact.recreated)}</Group>
      )}

      <p style={{ margin: 0, fontSize: 'var(--fz-12)', color: 'var(--fg-3)' }}>
        {t('clients:restore.managerKept')} {t('clients:restore.banksNotRecorded')}
      </p>
    </div>
  );
}
